import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import PageTitle from "../components/common/PageTitle";
import ProductCard from "../components/common/ProductCard";
import { apiService } from "../api/services/apiService";
import type { Category, ProductCatalogItem, UIProduct } from "../types";
import { EntityStatus, EntityType } from "../types";

function catalogItemToUIProduct(p: ProductCatalogItem): UIProduct {
  const categoryNames = (p.categoryNames ?? []).filter(Boolean);
  return {
    id: p.uuid,
    name: p.name,
    description: p.description || "No description available",
    category: categoryNames[0] ?? "Uncategorized",
    categoryNames,
    tags: p.tags ?? [],
    heroImage: p.heroImageName ?? null,
    status: p.status as EntityStatus,
    type: p.type as EntityType,
    subscribable: p.subscribable === true,
  };
}

const CategoryExplorerDetailPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const [category, setCategory] = useState<Category | null>(null);
  const [catalog, setCatalog] = useState<ProductCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!categoryId) return;
    setLoading(true);
    setError(null);
    Promise.all([
      apiService.getCategory(categoryId),
      apiService.getProductCatalog(),
    ])
      .then(([categoryData, catalogData]) => {
        setCategory(categoryData);
        setCatalog(catalogData);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load category");
      })
      .finally(() => setLoading(false));
  }, [categoryId]);

  const productsInCategory = useMemo(() => {
    if (!category) return [];
    return catalog.filter((product) =>
      (product.categoryNames ?? []).includes(category.name),
    );
  }, [catalog, category]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle={category?.name ?? "Category Details"}
        pageDescription={
          category?.description?.trim() ||
          "Browse products grouped under this category."
        }
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/category-explorer")}
          sx={{ mb: 3, textTransform: "none" }}
        >
          Back to Category Explorer
        </Button>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && category && (
          <>
            <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                <Chip
                  label={category.status}
                  size="small"
                  color={
                    category.status === EntityStatus.ACTIVE
                      ? "success"
                      : "default"
                  }
                />
                <Chip
                  label={`${productsInCategory.length} product${
                    productsInCategory.length === 1 ? "" : "s"
                  }`}
                  size="small"
                  variant="outlined"
                />
              </Box>
              {category.description?.trim() && (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {category.description}
                </Typography>
              )}
              {category.tags?.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {category.tags.map((tag) => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" />
                  ))}
                </Box>
              )}
            </Paper>

            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Products in this category
            </Typography>

            {productsInCategory.length > 0 ? (
              <Grid container spacing={3}>
                {productsInCategory.map((product) => (
                  <Grid item xs={12} sm={6} md={4} key={product.uuid}>
                    <ProductCard product={catalogItemToUIProduct(product)} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: "grey.300",
                }}
              >
                <Typography color="text.secondary">
                  No published products are linked to this category yet.
                </Typography>
              </Paper>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default CategoryExplorerDetailPage;
